import { ApiError, getCilChangeEvent, getCilChangeEventExplanation, getCilOrderSummary, getCilCoverage, getCilSummary, getCilTopReviewers, listCilChangeEvents, type ListCilChangeEventsParams } from "@hp/api-client";
import type { CilChangeEvent, CilExplanation, CilOrderSummary, CilRulingCoverage, CilSummary, CilTopReviewer } from "@hp/types";
import { CIL_CHANGE_EVENTS, CIL_EXPLANATIONS, CIL_SUMMARY } from "../mocks/fixtures";
import { USE_MOCK_DATA } from "./config";
import { simulateNetwork } from "./simulateNetwork";

/** GET /cil/summary - the two "Changed Events" dashboard counts. */
export function fetchCilSummary(): Promise<CilSummary> {
  if (!USE_MOCK_DATA) return getCilSummary();
  return simulateNetwork(CIL_SUMMARY);
}

/** GET /cil/change-events - the list behind each card. */
export function fetchCilChangeEvents(params: ListCilChangeEventsParams): Promise<CilChangeEvent[]> {
  if (!USE_MOCK_DATA) return listCilChangeEvents(params);
  return simulateNetwork(
    CIL_CHANGE_EVENTS.filter(
      (e) =>
        (!params.direction || e.direction === params.direction) &&
        (params.view !== "counted" || e.counts_on_card) &&
        (params.view !== "watching" || !e.counts_on_card),
    ),
  );
}

/** GET /cil/coverage - every ruling read and what became of it. */
export function fetchCilCoverage(): Promise<CilRulingCoverage[]> {
  if (!USE_MOCK_DATA) return getCilCoverage();
  return simulateNetwork([]);
}

/** GET /cil/change-events/{id}/order-summary - the HP order the event was compared with, consolidated. */
export function fetchCilOrderSummary(id: string): Promise<CilOrderSummary> {
  if (!USE_MOCK_DATA) return getCilOrderSummary(id);
  return simulateNetwork({ available: false, message: "Not available in the demo data.", order_label: null, file_name: null, cost_label: null, positions: [], lines: [], retired_copies: [] });
}

/** GET /cil/change-events/{id}/explanation - why a changed event exists. */
export function fetchCilExplanation(id: string): Promise<CilExplanation> {
  if (!USE_MOCK_DATA) return getCilChangeEventExplanation(id);
  const found = CIL_EXPLANATIONS[id];
  if (!found) return Promise.reject(new Error("No explanation in the demo data for this event"));
  return simulateNetwork(found);
}

/** GET /cil/change-events/{id} - one event for its own page; `null` when it no longer exists. */
export async function fetchCilChangeEvent(id: string): Promise<CilChangeEvent | null> {
  if (USE_MOCK_DATA) return simulateNetwork(CIL_CHANGE_EVENTS.find((e) => e.id === id) ?? null);
  try {
    return await getCilChangeEvent(id);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/** GET /cil/top-reviewers - who reviewed, confirmed, closed and rejected the most. */
export function fetchCilTopReviewers(): Promise<CilTopReviewer[]> {
  if (!USE_MOCK_DATA) return getCilTopReviewers(5);
  return simulateNetwork([
    { email: "a@hindustanpower.com", name: "Asha Rao", reviewed: 14, confirmed: 9, rejected: 3, closed: 21, total: 47 },
    { email: "b@hindustanpower.com", name: "Vikram Shah", reviewed: 8, confirmed: 5, rejected: 2, closed: 12, total: 27 },
  ]);
}
